import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles, badgeStyles } from '../styles/shared.js';
import { generationChanged } from '../utils/constants.js';
import type { ModelerController } from '../controllers/modeler-controller.js';

// ---------------------------------------------------------------------------
// <sysml-status-bar> — 24px bottom information bar (VS Code-style)
//
// Left:  error/warning badge (clickable), parse status
// Right: Ln/Col, "SysML v2" label, theme toggle
// ---------------------------------------------------------------------------

@customElement('sysml-status-bar')
export class SysmlStatusBar extends LitElement {
  static override styles = [
    baseStyles,
    badgeStyles,
    css`
      :host {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 24px;
        padding: 0 var(--r5-space-2);
        border-top: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
        flex-shrink: 0;
        font-size: 11px;
        color: var(--r5-text-muted);
        gap: var(--r5-space-3);
      }
      .left, .right {
        display: flex;
        align-items: center;
        gap: var(--r5-space-3);
      }
      .sb-btn {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 0 var(--r5-space-1);
        height: 20px;
        border: none;
        border-radius: var(--r5-radius-sm);
        background: none;
        color: var(--r5-text-muted);
        font-size: 11px;
        font-family: inherit;
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
      }
      .sb-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
      }
      .sb-btn.has-errors { color: var(--r5-destructive-700); }
      .sb-btn.has-warnings { color: var(--r5-warning-700); }
      .cursor-info {
        font-family: var(--r5-font-mono);
        font-size: 11px;
      }
      .lang-label {
        font-weight: 500;
      }
      .theme-icon {
        width: 12px;
        height: 12px;
        stroke: currentColor;
        fill: none;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    `,
  ];

  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;
  @property({ attribute: false }) theme: 'light' | 'dark' = 'dark';

  override render() {
    const m = this.modeler;
    const errors = m.errorCount;
    const warnings = m.warningCount;

    return html`
      <div class="left" role="status" aria-label="Editor status">
        <button
          class="sb-btn ${errors > 0 ? 'has-errors' : warnings > 0 ? 'has-warnings' : ''}"
          @click=${this._onToggleDiagnostics}
          title="Toggle Diagnostics"
        >
          ${errors > 0
            ? html`<ark-icon name="x-circle" size="xs"></ark-icon> ${errors}`
            : nothing}
          ${warnings > 0
            ? html`<ark-icon name="alert-triangle" size="xs"></ark-icon> ${warnings}`
            : nothing}
          ${errors === 0 && warnings === 0
            ? html`<ark-icon name="check-circle" size="xs"></ark-icon> 0`
            : nothing}
        </button>

        ${m.isParsing
          ? html`<span><ark-spinner size="xs"></ark-spinner></span>`
          : m.parseSuccess
            ? html`<span>${m.blockCount} blocks</span>`
            : nothing}
      </div>

      <div class="right">
        <span class="cursor-info">Ln ${m.cursorPosition.line}, Col ${m.cursorPosition.column}</span>
        <span class="lang-label">SysML v2</span>
        <button class="sb-btn" @click=${this._onToggleTheme} title=${this.theme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}>
          ${this.theme === 'dark'
            ? html`<svg class="theme-icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`
            : html`<svg class="theme-icon" viewBox="0 0 24 24"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`}
        </button>
      </div>
    `;
  }

  private _onToggleDiagnostics(): void {
    this.dispatchEvent(new CustomEvent('toggle-diagnostics', { bubbles: true, composed: true }));
  }

  private _onToggleTheme(): void {
    this.dispatchEvent(new CustomEvent('toggle-theme', { bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-status-bar': SysmlStatusBar;
  }
}
